import { Grid } from '@mantine/core';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { Textarea } from 'react-hook-form-mantine';
import ReactPlayer from 'react-player/lazy';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { When } from '../../../../../react-commons/components/control-flow/When';
import { Section } from '../../../../../react-commons/components/data-display/Section';
import { Form_Markdown } from '../../../../../react-commons/components/form/inputs/Form_Markdown';
import { Form_RichText } from '../../../../../react-commons/components/form/inputs/Form_RichText';
import { useSubtypeFormNames } from '../../../../../react-commons/hooks/useSubtypeFormNames';
import { useWatchSubtypeProperties } from '../../../../../react-commons/hooks/useWatchSubtypeProperties';
import type { FormNames } from '../../../../../react-commons/utils/form/getFormNames';
import { ContentSectionContentType } from '../../../resource/enumerations/ContentSectionContentType';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    ContentSectionContentPropertyValidation,
    type ContentSectionContentResource,
    contentSectionContentDefaultSubtypeValues,
    contentSectionContentSubTypeProperties,
} from '../../../resource/resources/artifacts/content/contentSectionContentResource';
import { PropertyInputs } from '../../reusable-components/base/property-bases/PropertyInputs';
import { ControlledPropertyInputs } from '../../reusable-components/base/property-bases/read-only/ControlledPropertyInputs';
import { ReferenceInputs } from '../../reusable-components/base/reference-text-fields/ReferenceInputs';
import { FormImageContentDisplay } from './FormImageContentDisplay';
import { FormVideoContentDisplay } from './FormVideoContentDisplay';

export const ContentSectionContentContent = ({
    id,
    versionId,
    form,
    formNames,
    readOnly,
}: {
    id: string | undefined;
    versionId: string;
    form: UseFormReturn<ContentSectionContentResource>;
    formNames: FormNames<ContentSectionContentResource>;
    readOnly: boolean;
    displayedInOverview?: boolean;
}) => {
    const subtypeFormNames = useSubtypeFormNames({
        form,
        typeName: formNames.type,
        subtypeProperties: contentSectionContentSubTypeProperties,
        defaultSubtypeValues: contentSectionContentDefaultSubtypeValues,
    });

    const [type, decorator] = useWatch({
        control: form.control,
        name: [formNames.type, formNames.decorator],
    });
    const [url] = useWatchSubtypeProperties({
        form,
        names: [subtypeFormNames.url],
    });

    return (
        <>
            <Section label={'Decorator'}>
                <Grid>
                    <Grid.Col span={6}>
                        <ControlledPropertyInputs.Number
                            readOnly={readOnly}
                            labelOverride={'Weight'}
                            nullable={false}
                            value={decorator.weight}
                            onChange={(value) => {
                                form.setValue(
                                    formNames.decorator,
                                    { weight: value, height: decorator.height },
                                    { shouldDirty: true }
                                );
                            }}
                            propertyValidationInformation={{
                                propertyIdentifier: ContentSectionContentPropertyValidation.DECORATOR,
                                resourceType: ResourceType.CONTENT_SECTION_CONTENT,
                                resourceId: id ?? null,
                            }}
                        />
                    </Grid.Col>
                    {[
                        ContentSectionContentType.VIDEO_URL,
                        ContentSectionContentType.IMAGE_ATTACHMENT,
                        ContentSectionContentType.VIDEO_ATTACHMENT,
                    ].includes(type) && (
                        <Grid.Col span={6}>
                            <ControlledPropertyInputs.Number
                                readOnly={readOnly}
                                labelOverride={'Height (in px)'}
                                nullable={false}
                                value={decorator.height}
                                onChange={(value) => {
                                    form.setValue(
                                        formNames.decorator,
                                        { height: value, weight: decorator.weight },
                                        { shouldDirty: true }
                                    );
                                }}
                                propertyValidationInformation={{
                                    propertyIdentifier: ContentSectionContentPropertyValidation.DECORATOR,
                                    resourceType: ResourceType.CONTENT_SECTION_CONTENT,
                                    resourceId: id ?? null,
                                }}
                            />
                        </Grid.Col>
                    )}
                </Grid>
            </Section>
            <When
                expression={type}
                cases={{
                    [ContentSectionContentType.PLAIN_TEXT]: (
                        <Show when={subtypeFormNames.text}>
                            {(text) => (
                                <Textarea
                                    name={text}
                                    control={form.control}
                                    rows={18}
                                    placeholder={'not defined'}
                                    readOnly={readOnly}
                                />
                            )}
                        </Show>
                    ),
                    [ContentSectionContentType.RICH_TEXT]: (
                        <Show when={subtypeFormNames.text}>
                            {(text) => <Form_RichText name={text} form={form} readonly={readOnly} />}
                        </Show>
                    ),
                    [ContentSectionContentType.MARKDOWN]: (
                        <Show when={subtypeFormNames.text}>
                            {(text) => <Form_Markdown name={text} form={form} readonly={readOnly} />}
                        </Show>
                    ),
                    [ContentSectionContentType.IMAGE_ATTACHMENT]: (
                        <Show when={subtypeFormNames.image}>
                            {(image) => (
                                <>
                                    <ReferenceInputs.ImageAttachment
                                        projectVersionId={versionId}
                                        form={form}
                                        propertyValidationInformation={{
                                            propertyIdentifier: ContentSectionContentPropertyValidation.IMAGE,
                                            resourceType: ResourceType.CONTENT_SECTION_CONTENT,
                                            resourceId: id ?? null,
                                        }}
                                        readOnly={readOnly}
                                        nullable={false}
                                        name={image}
                                    />
                                    <FormImageContentDisplay projectVersionId={versionId} form={form} name={image} />
                                </>
                            )}
                        </Show>
                    ),
                    [ContentSectionContentType.VIDEO_ATTACHMENT]: (
                        <Show when={subtypeFormNames.video}>
                            {(video) => (
                                <>
                                    <ReferenceInputs.VideoAttachment
                                        projectVersionId={versionId}
                                        form={form}
                                        propertyValidationInformation={{
                                            propertyIdentifier: ContentSectionContentPropertyValidation.VIDEO,
                                            resourceType: ResourceType.CONTENT_SECTION_CONTENT,
                                            resourceId: id ?? null,
                                        }}
                                        readOnly={readOnly}
                                        nullable={false}
                                        name={video}
                                    />
                                    <FormVideoContentDisplay projectVersionId={versionId} form={form} name={video} />
                                </>
                            )}
                        </Show>
                    ),
                    [ContentSectionContentType.VIDEO_URL]: (
                        <Show when={subtypeFormNames.url}>
                            {(urlName) => (
                                <>
                                    <PropertyInputs.String
                                        control={form.control}
                                        propertyValidationInformation={{
                                            propertyIdentifier: ContentSectionContentPropertyValidation.URL,
                                            resourceType: ResourceType.CONTENT_SECTION_CONTENT,
                                            resourceId: id ?? null,
                                        }}
                                        readOnly={readOnly}
                                        nullable={true}
                                        name={urlName}
                                    />
                                    {url && <ReactPlayer url={url} controls />}
                                </>
                            )}
                        </Show>
                    ),
                }}
            />
        </>
    );
};
